import { debounce } from 'lodash-es';
import { useEffect, useMemo, useRef, useState } from 'react';
import { SalesforceSupplierSearchResult, VendorLookupSearchResult } from 'sb/api/vendor';

type UseReconciliationSearchArgs = {
  searchVendorLookup: (str: string) => Promise<VendorLookupSearchResult[]>;
  searchSalesforceSuppliers: (str: string) => Promise<SalesforceSupplierSearchResult[]>;
  onLockedSearchCleared: () => void;
  onVendorSearchCleared: () => void;
};

export const useReconciliationSearch = ({
  searchVendorLookup,
  searchSalesforceSuppliers,
  onLockedSearchCleared,
  onVendorSearchCleared
}: UseReconciliationSearchArgs) => {
  const [searchesLocked, setSearchesLocked] = useState(true);
  const [lockedSearchTerm, setLockedSearchTerm] = useState('');
  const [vendorSearchTerm, setVendorSearchTerm] = useState('');
  const [salesforceSearchTerm, setSalesforceSearchTerm] = useState('');

  const [vendorLookupSearchLoading, setVendorLookupSearchLoading] = useState(false);
  const [vendorLookupResults, setVendorLookupResults] = useState<VendorLookupSearchResult[]>([]);
  const [salesforceSupplierSearchLoading, setSalesforceSupplierSearchLoading] = useState(false);
  const [salesforceSupplierResults, setSalesforceSupplierResults] = useState<SalesforceSupplierSearchResult[]>([]);

  const searchVendorLookupRef = useRef(searchVendorLookup);
  const searchSalesforceSuppliersRef = useRef(searchSalesforceSuppliers);

  useEffect(() => {
    searchVendorLookupRef.current = searchVendorLookup;
  }, [searchVendorLookup]);

  useEffect(() => {
    searchSalesforceSuppliersRef.current = searchSalesforceSuppliers;
  }, [searchSalesforceSuppliers]);

  const effectiveVendorSearchTerm = searchesLocked ? lockedSearchTerm : vendorSearchTerm;
  const effectiveSalesforceSearchTerm = searchesLocked ? lockedSearchTerm : salesforceSearchTerm;

  const handleSalesforceSupplierSearch = useMemo(
    () =>
      debounce(async (str: string) => {
        const trimmedSearchTerm = str.trim();
        if (trimmedSearchTerm.length < 2) {
          setSalesforceSupplierResults([]);
          setSalesforceSupplierSearchLoading(false);
          return;
        }

        setSalesforceSupplierSearchLoading(true);
        try {
          const res = await searchSalesforceSuppliersRef.current(trimmedSearchTerm);
          setSalesforceSupplierResults(res);
        } catch {
          setSalesforceSupplierResults([]);
        } finally {
          setSalesforceSupplierSearchLoading(false);
        }
      }, 250),
    []
  );

  const handleVendorLookupSearch = useMemo(
    () =>
      debounce(async (str: string) => {
        const trimmedSearchTerm = str.trim();
        if (trimmedSearchTerm.length < 2) {
          setVendorLookupResults([]);
          setVendorLookupSearchLoading(false);
          return;
        }

        setVendorLookupSearchLoading(true);
        try {
          const res = await searchVendorLookupRef.current(trimmedSearchTerm);
          setVendorLookupResults(res);
        } catch {
          setVendorLookupResults([]);
        } finally {
          setVendorLookupSearchLoading(false);
        }
      }, 325),
    []
  );

  useEffect(() => {
    return () => {
      handleVendorLookupSearch.cancel();
      handleSalesforceSupplierSearch.cancel();
    };
  }, [handleVendorLookupSearch, handleSalesforceSupplierSearch]);

  useEffect(() => {
    handleVendorLookupSearch(effectiveVendorSearchTerm);
  }, [effectiveVendorSearchTerm, handleVendorLookupSearch]);

  useEffect(() => {
    handleSalesforceSupplierSearch(effectiveSalesforceSearchTerm);
  }, [effectiveSalesforceSearchTerm, handleSalesforceSupplierSearch]);

  const handleSearchLockToggle = (isLocked: boolean) => {
    if (!searchesLocked && isLocked) {
      const mergedSearchTerm = vendorSearchTerm || salesforceSearchTerm;
      setLockedSearchTerm(mergedSearchTerm);
    }
    if (searchesLocked && !isLocked) {
      setVendorSearchTerm(lockedSearchTerm);
      setSalesforceSearchTerm(lockedSearchTerm);
    }
    setSearchesLocked(isLocked);
  };

  const handleLockedSearchChange = (value: string) => {
    setLockedSearchTerm(value);
    if (!value.trim()) {
      setVendorLookupResults([]);
      setSalesforceSupplierResults([]);
      onLockedSearchCleared();
    }
  };

  const handleUnlockedVendorSearchChange = (value: string) => {
    setVendorSearchTerm(value);
    if (!value.trim()) {
      setVendorLookupResults([]);
      onVendorSearchCleared();
    }
  };

  const refreshVantagePane = async () => {
    const trimmedSearchTerm = effectiveVendorSearchTerm.trim();
    if (trimmedSearchTerm.length < 2) {
      setVendorLookupResults([]);
      return;
    }

    setVendorLookupSearchLoading(true);
    try {
      const refreshedResults = await searchVendorLookup(trimmedSearchTerm);
      setVendorLookupResults(refreshedResults);
    } finally {
      setVendorLookupSearchLoading(false);
    }
  };

  return {
    searchesLocked,
    lockedSearchTerm,
    vendorSearchTerm,
    salesforceSearchTerm,
    vendorLookupSearchLoading,
    vendorLookupResults,
    salesforceSupplierSearchLoading,
    salesforceSupplierResults,
    effectiveSalesforceSearchTerm,
    effectiveVendorSearchTerm,
    setSalesforceSearchTerm,
    setSalesforceSupplierResults,
    setSalesforceSupplierSearchLoading,
    handleSearchLockToggle,
    handleLockedSearchChange,
    handleUnlockedVendorSearchChange,
    refreshVantagePane
  };
};
